const rand = (a, b) => a + Math.random() * (b - a);

const unit = () => {
  const z = rand(-1, 1), t = rand(0, Math.PI * 2), r = Math.sqrt(1 - z * z);
  return [r * Math.cos(t), z, r * Math.sin(t)];
};

const hemisphere = (side) => {
  const [ux, uy, uz] = unit();
  const ripple = 1 + 0.055 * Math.sin(9 * uz + 4 * uy) * Math.cos(7 * uy - 5 * ux) + 0.03 * Math.sin(17 * uz - 11 * uy);
  let x = ux * 0.42 * ripple;
  let y = uy * 0.6 * ripple;
  const z = uz * 0.95 * ripple;
  if (ux * side < 0) x *= 0.3;
  if (y < -0.22) y = -0.22 + (y + 0.22) * 0.45;
  return [side * 0.45 + x, y + 0.08, z];
};

const cerebellum = () => {
  const [ux, uy, uz] = unit();
  const r = 1 + 0.05 * Math.sin(34 * uy);
  return [ux * 0.52 * r, -0.36 + uy * 0.2 * r, -0.62 + uz * 0.28 * r];
};

const stem = () => {
  const t = Math.random(), a = rand(0, Math.PI * 2), r = 0.1 * (1 - t * 0.3);
  return [Math.cos(a) * r, -0.42 - t * 0.5, -0.32 - t * 0.12 + Math.sin(a) * r];
};

const sample = (count) =>
  Array.from({ length: count }, () => {
    const p = Math.random();
    if (p < 0.8) return hemisphere(Math.random() < 0.5 ? -1 : 1);
    if (p < 0.94) return cerebellum();
    return stem();
  });

const fibonacci = (n) =>
  Array.from({ length: n }, (_, i) => {
    const y = 1 - (2 * (i + 0.5)) / n, r = Math.sqrt(1 - y * y), a = i * Math.PI * (3 - Math.sqrt(5));
    return [Math.cos(a) * r, y, Math.sin(a) * r];
  });

export const buildBrain = (mobile) => buildAssembly(sample(mobile ? 150 : 360), 0.24, 0.7);
export const buildGlobeNet = (mobile) => buildAssembly(fibonacci(mobile ? 200 : 500), mobile ? 0.32 : 0.2, 0.45);

const buildAssembly = (targets, linkDist, spread) => {
  const nodes = targets.map(([tx, ty, tz]) => ({
    tx, ty, tz,
    fx: rand(-2.6, 2.6), fy: rand(-1.6, 1.6), fz: rand(-1.2, 1.2),
    vx: rand(-0.12, 0.12), vy: rand(-0.12, 0.12), vz: rand(-0.08, 0.08),
    delay: rand(0, spread), glow: 0,
  }));
  const seen = new Set();
  const links = [];
  nodes.forEach((a, i) => {
    nodes
      .map((b, j) => ({ j, d: Math.hypot(a.tx - b.tx, a.ty - b.ty, a.tz - b.tz) }))
      .filter((o) => o.j !== i && o.d < linkDist)
      .sort((p, q) => p.d - q.d)
      .slice(0, 3)
      .forEach(({ j }) => {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (!seen.has(key)) {
          seen.add(key);
          links.push([i, j]);
        }
      });
  });
  const floatLinks = nodes.flatMap((_, i) => [0, 1, 2].map(() => [i, Math.floor(Math.random() * nodes.length)]));
  return { nodes, links, floatLinks, pulses: [] };
};

export const BRAIN_TIMING = { float: 0.8, form: 3.6, formedAt: 5.1 };
export const GLOBE_TIMING = { float: 0.15, form: 1.7, formedAt: 2.3 };
const easeIO = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
export const formK = (n, t, tm) => easeIO(Math.min(Math.max((t - tm.float - n.delay) / tm.form, 0), 1));

export const stepBrain = (brain, dt, maxPulses, formed) => {
  brain.nodes.forEach((n) => {
    n.fx += n.vx * dt;
    n.fy += n.vy * dt;
    n.fz += n.vz * dt;
    if (Math.abs(n.fx) > 2.8) n.vx *= -1;
    if (Math.abs(n.fy) > 1.8) n.vy *= -1;
    if (Math.abs(n.fz) > 1.3) n.vz *= -1;
    n.glow *= 0.96;
  });
  if (!formed) return;
  const { pulses, links, nodes } = brain;
  if (pulses.length < maxPulses && Math.random() < 0.2) {
    const l = links[Math.floor(Math.random() * links.length)];
    const dir = Math.random() < 0.5;
    pulses.push({ from: dir ? l[0] : l[1], to: dir ? l[1] : l[0], t: 0, v: rand(0.02, 0.045) });
  }
  for (let i = pulses.length - 1; i >= 0; i--) {
    const p = pulses[i];
    p.t += p.v;
    if (p.t >= 1) {
      nodes[p.to].glow = 1;
      pulses.splice(i, 1);
    }
  }
};
